/**
 * Wizard Component Type Definitions
 */

/**
 * What a completion button does after its linked interface-action create succeeds:
 * - `close`: persist the submission and close the runner modal.
 * - `reset`: persist the submission and reset the form for another entry (modal stays open).
 * - `navigate`: persist the submission, close the modal and navigate to `navigateTo`, forwarding
 *   the just-created target-entry id as the `userId` query param.
 */
export const CompletionBehaviorEnum = <const>{
  Close: 'close',
  Reset: 'reset',
  Navigate: 'navigate',
};
export type CompletionBehavior = (typeof CompletionBehaviorEnum)[keyof typeof CompletionBehaviorEnum];

/**
 * Optional visibility rule for a completion button: the button only renders when the form-model
 * value at `fieldKeyPath` equals `value`. The key path is `entry.`-prefixed and resolved against
 * the wizard form model the same way condition fields elsewhere are (wrap the model in `{ entry }`).
 */
export interface CompletionActionCondition {
  /** Form-model key path, e.g. `entry.start.choice`. */
  fieldKeyPath: string;
  /** Required value at `fieldKeyPath` for the button to show. */
  value: string;
}

/**
 * A per-form configured completion button. Each item runs its linked interface-action create via
 * the runner, then acts per `behavior` (close / reset / navigate). Used on the wizard's last step
 * (props.completionActions) and on flat create-forms (SourceFormSettings.formActions).
 */
export type CompletionAction = {
  interfaceActionId: string;
  label: string;
  behavior: CompletionBehavior;
  interfaceActionModuleCode?: string;
  /** Button appearance: 'filled' (primary, default) or 'outline' (secondary). */
  buttonStyle?: 'filled' | 'outline';
  /** Only render this button when the form model matches this condition. Omit to always show. */
  condition?: CompletionActionCondition;
  /** For behavior 'navigate': route to navigate to after the create+persist, e.g. '/onboarding'. */
  navigateTo?: string;
};

/**
 * Runtime signal a wizard hands to its completion handler (formState.onWizardComplete):
 * - `close`: persist submission + close.
 * - `addAnother`: persist submission, keep open (wizard resets itself via clearForm).
 * - `navigate`: persist submission + close, then navigate (see WizardCompletionNavigation).
 */
export type WizardCompletionMode = 'close' | 'addAnother' | 'navigate';

/** Navigation details forwarded with a `navigate`-mode completion. */
export interface WizardCompletionNavigation {
  /** Route to navigate to after a successful create + persist, e.g. '/onboarding'. */
  navigateTo?: string;
  /** Id of the just-created target entry, forwarded as the `userId` query param. */
  entryId?: string;
}

/** One veld-regel in de review-stap: label + leesbare waarde, of leeg (badge Ontbreekt/Optioneel). */
export interface ReviewRow {
  label: string;
  required: boolean;
  /** Geformatteerde waarde; `null` = leeg veld, de badge neemt het over. */
  value: string | null;
}

/** Samenvatting van één wizard-stap in de review-stap, incl. de stap-index voor de Bewerken-knop. */
export interface ReviewSection {
  stepIndex: number;
  label: string;
  rows: ReviewRow[];
}

/** Eén stap in de custom wizard-stepper header — alleen het label wordt getoond. */
export interface WizardStepperItem {
  label: string;
}

/**
 * Onderliggende status van één wizard-stap, afgeleid uit de ingevulde data (Figma "Interactive
 * wizard steps", node 2353:36776). Staat LOS van "actief": actief is een kleur-overlay die de
 * status nooit vervangt.
 *
 * - `not-started`: nog niets ingevuld in deze stap.
 * - `incomplete`: deels ingevuld, maar nog niet geldig (verplichte velden ontbreken).
 * - `complete`: alles wat nodig is staat erin.
 *
 * Het design maakt bewust GEEN onderscheid tussen "nooit geopend" en "geopend maar leeg gelaten":
 * in een flow waar niets geblokkeerd is zegt dat verschil de gebruiker niets.
 */
export const WizardStepStatusEnum = <const>{
  NotStarted: 'not-started',
  Incomplete: 'incomplete',
  Complete: 'complete',
};
export type WizardStepStatus = (typeof WizardStepStatusEnum)[keyof typeof WizardStepStatusEnum];

/**
 * Which glyph the 20px badge shows. `number` = the step number, no icon.
 */
export type WizardStepperBadgeIcon = 'number' | 'dashed' | 'check';

/** Afgeleide weergave van één stepper-stap: status, actief-overlay, glyph en klikbaarheid. */
export interface WizardStepperView {
  index: number;
  label: string;
  status: WizardStepStatus;
  /** Dit is de stap die nu open staat — puur een kleur-overlay bovenop {@link status}. */
  active: boolean;
  icon: WizardStepperBadgeIcon;
  clickable: boolean;
  /**
   * Unreachable RIGHT NOW because an earlier step blocks progress (linear/positional model only).
   * Separate from `!clickable`, which is also false for the display-only indicator, where nothing is
   * locked and a lock marker would be a lie.
   */
  blocked: boolean;
  /**
   * What the host has to say about this step (a reviewer's remark, say), rendered verbatim as the
   * tooltip and folded into {@link ariaLabel}; null when the host said nothing. The stepper never
   * composes this text — it has no idea what the notice is about.
   */
  notice: string | null;
  /** "{step name}, {status}" — a screen reader never sees the check/dash/number glyph. */
  ariaLabel: string;
}
